'use client'

import React from 'react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface Tab {
  key: string
  label: string
}

interface InspectionTabsProps {
  tabs: Tab[]
  contents: Record<string, React.ReactNode>
}

/**
 * Vertical switcher for the Inspection Times block.
 *
 * Uses the canonical Tabs component (`src/components/ui/tabs.tsx`, Figma tab
 * 2198:41383 / tabs 2198:41426) — the previous hand-rolled implementation was an
 * underline tab bar with a JS-measured sliding indicator, `border-gray-200` and
 * hardcoded `#000000`, none of which exist in the design system.
 *
 * Labels are uppercased to match every `tabs` instance in the Figma file
 * (e.g. 4081:78476 on the Residential listings page renders SELL / BUY / MANAGE /
 * RENT); the component itself deliberately does not force caps.
 *
 * Panels use `forceMount` so all verticals stay in the DOM exactly as before —
 * the old markup rendered every panel and hid the inactive ones with `hidden`.
 */
export function InspectionTabs({ tabs, contents }: InspectionTabsProps) {
  if (tabs.length <= 1) {
    return <>{contents[tabs[0]?.key || '']}</>
  }

  return (
    <Tabs className="flex flex-col gap-8" defaultValue={tabs[0]?.key || ''}>
      <TabsList aria-label="Inspection type">
        {tabs.map((tab) => (
          <TabsTrigger className="uppercase" key={tab.key} value={tab.key}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.map((tab) => (
        <TabsContent forceMount key={tab.key} value={tab.key}>
          {contents[tab.key]}
        </TabsContent>
      ))}
    </Tabs>
  )
}
